import type { Agent } from '../../types';

/** The drawer lists custom agents (built by the tenant in Moo Studio) apart
 *  from the standard ones. Only the custom group carries a label; the
 *  standard agents are simply the agents. `category` comes from the host and
 *  only `'custom'` marks a custom agent. */
export interface AgentGroup {
  readonly key: 'system' | 'custom';
  readonly label?: string;
  readonly ids: readonly string[];
}

/** Split `ids` into the standard group and the custom group, keeping each
 *  group in the given order and dropping empty groups. */
export function groupAgents(
  ids: readonly string[],
  byId: Readonly<Record<string, Agent | undefined>>
): AgentGroup[] {
  const system: string[] = [];
  const custom: string[] = [];
  for (const id of ids) {
    const agent = byId[id];
    if (!agent) continue;
    if (agent.category === 'custom') {
      custom.push(id);
    } else {
      system.push(id);
    }
  }
  const groups: AgentGroup[] = [
    { key: 'system', ids: system },
    { key: 'custom', label: 'Custom agents', ids: custom },
  ];
  return groups.filter((group) => group.ids.length > 0);
}

/**
 * The drawer's short list: every standard agent, and no custom agents; those
 * are under "Browse all agents". The one exception is a custom agent that is
 * selected, which stays listed so the drawer still shows what is in use.
 * `hidden` is how many agents only Browse all shows.
 */
export function shortListGroups(
  groups: readonly AgentGroup[],
  selectedId?: string | null
): { readonly groups: AgentGroup[]; readonly hidden: number } {
  const shown = groups
    .map((g) => ({
      ...g,
      ids:
        g.key === 'custom'
          ? g.ids.filter((id) => id === selectedId)
          : [...g.ids],
    }))
    .filter((g) => g.ids.length > 0);
  const total = groups.reduce((n, g) => n + g.ids.length, 0);
  const listed = shown.reduce((n, g) => n + g.ids.length, 0);
  return { groups: shown, hidden: total - listed };
}
